import {ReadOnlyService} from '../_services/read-only.service';
import {AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CalendarOptions} from '@fullcalendar/core';
import * as moment from 'moment';
import {EventNewComponent} from './modals/event-new/event-new.component';
import {EventEditComponent} from './modals/event-edit/event-edit.component';
import {EventService} from '../_services/event.service';
import {BetweenDates, Event, EventTO, ExtendedProps, Monitor, Unavailability, UNAVAILABILITY_REASONS} from '../models/eventRequest';
import {UnavailabilityService} from '../_services/unavailability.service';
import {UnavailabilityFormComponent} from './modals/unavailability-form/unavailability-form.component';
import {MonitorService} from '../_services/monitor.service';
import {EventsMonitorComponent} from './modals/events-monitor/events-monitor.component';
import {Router} from '@angular/router';

import dayGridPlugin from '@fullcalendar/daygrid'; // a plugin
import timeGridPlugin from '@fullcalendar/timegrid'; // vues jour / semaine (plugin gratuit, sans licence)
import interactionPlugin from '@fullcalendar/interaction'; // a plugin
import frLocale from '@fullcalendar/core/locales/fr';

import {
  ABSENT_MONITOR_ID,
  COLOR_AUTOMATIC_AVAILABLE,
  COLOR_AUTOMATIC_RESERVED,
  COLOR_MANUAL_AVAILABLE,
  COLOR_MANUAL_RESERVED,
  COLOR_PERMIS
} from '../global.variables';

declare let $: any;

/** Plage horaire habituelle des heures de conduite (élargie si une heure tombe en dehors). */
const DEFAULT_FIRST_HOUR = 8;
const DEFAULT_LAST_HOUR = 21; // fin de la dernière heure (créneau 20h-21h visible)
const DEFAULT_SLOT_MIN = '08:00:00';
const DEFAULT_SLOT_MAX = '21:00:00';
/** Hauteur minimale du calendrier (px) : créneaux 8h-20h lisibles. */
const MIN_CALENDAR_HEIGHT = 700;

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.css']
})
export class CalendarComponent implements OnInit, AfterViewInit, OnDestroy {

  /** Le menu mobile qui s'ouvre / se ferme déplace le calendrier : on recalcule sa hauteur. */
  private readonly onNavbarToggle = () => this.fitHeight();
  /** Recalcule la hauteur dès que la mise en page bouge (polices chargées, menu, bandeaux...). */
  private layoutObserver?: ResizeObserver;

  popupError?: string;
  calendarEvents: any[] = [];
  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
  };
  newEventDriving: any;
  editEventDriving: any;
  monitorId: string = "";
  monitors: Monitor[] = [];
  calendarInfos: any[] = [];

  /** Fiche du moniteur connecté (moniteur lecteur sur le planning global). */
  private myMonitor: Monitor | null = null;

  /** Indisponibilités des moniteurs affichées en gris hachuré (séparées des heures de conduite). */
  private unavailabilityEvents: any[] = [];
  private currentRange: any;
  /** Message après enregistrement d'une indisponibilité (créneaux libres supprimés...). */
  infoMessage = '';

  @ViewChild(EventNewComponent) eventNewComponent: EventNewComponent | undefined
  @ViewChild(EventEditComponent) eventEditComponent: EventEditComponent | undefined
  @ViewChild(EventsMonitorComponent) eventsMonitorComponent: EventsMonitorComponent | undefined
  @ViewChild(UnavailabilityFormComponent) unavailabilityForm: UnavailabilityFormComponent | undefined


  handleContentHeight() : number {
      if(screen.width < 577) {
        return 500
      } else {
        return MIN_CALENDAR_HEIGHT
      }
  }

  constructor(private eventService: EventService, private monitorService: MonitorService, private router: Router, public readOnlyService: ReadOnlyService,
              private elementRef: ElementRef, private zone: NgZone, private unavailabilityService: UnavailabilityService) {

    if (this.router.url.match("/monitor-calendar")) {
      monitorService.getMyProfile().subscribe((data:Monitor) => {
        this.monitors = [data];
      }, error => {
        console.log(error)
      });
    } else if (this.router.url.match("/monitors/[0-9]+")) {
      this.monitorId = this.router.url.split("/")[2];
      monitorService.getMonitorById(this.monitorId).subscribe((data:Monitor) => {
        this.monitors = [data];
      }, error => {
        console.log(error)
      });
    } else {
      monitorService.getAllMonitors().subscribe((data:Monitor[]) => {
        this.monitors = data.filter(monitor => monitor.enabled === true);
      }, error => {
        console.log(error)
      })
      if (this.isRestrictedToOwn() && this.readOnlyService.hasRole('ROLE_MONITOR')) {
        monitorService.getMyProfile().subscribe((data: Monitor) => this.myMonitor = data);
      }
    }
  }

  ngOnInit(): void {
    if (this.monitors.length == 1) {
      this.monitorId = String(this.monitors[0].id);
    }

    this.calendarOptions = {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      contentHeight: this.handleContentHeight(),
      initialView: 'timeGridWeek', // semaine en blocs horaires par défaut
      // deux vues « Semaine » : libellés distincts pour les différencier
      views: {
        timeGridWeek: {buttonText: 'Semaine (horaires)'},
        dayGridWeek: {buttonText: 'Semaine (liste)'}
      },
      validRange: {
        start: '2025-09-01',
        end: '2099-12-31'
      },
      businessHours: {
        // days of week. an array of zero-based day of week integers (0=Sunday)
        daysOfWeek: [ 1, 2, 3, 4, 5, 6, 7 ],
        startTime: '08:00',
        endTime: '20:00'
      },
      allDaySlot: false,
      // plage horaire affichée : ajustée aux heures de la période (voir fitSlotRange)
      slotMinTime: DEFAULT_SLOT_MIN,
      slotMaxTime: DEFAULT_SLOT_MAX,
      expandRows: true, // les créneaux s'étirent pour remplir toute la hauteur
      eventClick: this.handleEventClick.bind(this),
      dateClick: this.handleDateClick.bind(this), // bind is important!
      events: this.calendarEvents,
      weekends: true,
      locale: frLocale, // boutons et libellés en français ("Aujourd'hui", "Semaine"...)
      firstDay: 1,
      //businessHours: true,
      fixedWeekCount:false,
      editable:false, // drag and drop,
      dayMaxEvents: true,
      customButtons: {
        generateMonitorCalendar: {
          text: 'Créer des heures',
          click: this.handleMonitorEvents.bind(this)
        },
        getCalendarInformations: {
          text: 'Informations',
          click: this.handleGetInformations.bind(this)
        },
        addUnavailability: {
          text: 'Indisponibilité',
          hint: 'Déclarer un congé, un rendez-vous... (aucune heure possible pour le moniteur)',
          click: () => this.zone.run(() => this.openNewUnavailability())
        }
      },
      headerToolbar: {
        left: 'prev,next today getCalendarInformations' + (this.canManageUnavailability() ? ' addUnavailability' : ''),
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,dayGridWeek,timeGridDay',
      },
      datesSet: this.handleDateChange.bind(this)
    }

    if(this.isMobilePhone()) {
      // Mobile : flèches de part et d'autre d'un titre court, actions secondaires sous le calendrier
      this.calendarOptions.initialView = "timeGridDay"
      this.calendarOptions.headerToolbar = {left: 'prev', center: 'title', right: 'next'}
      this.calendarOptions.footerToolbar = {left: 'today',
        right: (this.canManageUnavailability() ? 'addUnavailability ' : '') + 'getCalendarInformations'}
      this.calendarOptions.titleFormat = {weekday: 'short', day: 'numeric', month: 'short'}
    }
    if (this.isFullPage()) {
      this.calendarOptions.windowResize = () => this.zone.run(() => this.fitHeight())
    }
  }

  ngAfterViewInit(): void {
    if (this.isFullPage()) {
      // après le rendu : la position du calendrier dans la page est connue
      setTimeout(() => this.fitHeight());
      $(document).on('shown.bs.collapse hidden.bs.collapse', '#navbarNavDropdown', this.onNavbarToggle);
      if (typeof ResizeObserver !== 'undefined') {
        // callback hors zone Angular : on y revient pour que la nouvelle hauteur soit appliquée
        this.layoutObserver = new ResizeObserver(() => this.zone.run(() => this.fitHeight()));
        const navbar = document.querySelector('nav.navbar');
        if (navbar) {
          this.layoutObserver.observe(navbar);
        }
      }
    }
  }

  ngOnDestroy(): void {
    $(document).off('shown.bs.collapse hidden.bs.collapse', '#navbarNavDropdown', this.onNavbarToggle);
    this.layoutObserver?.disconnect();
  }

  /** Pages planning (/calendar, /monitor-calendar) : le calendrier occupe tout l'écran. Intégré à une fiche : hauteur fixe. */
  private isFullPage(): boolean {
    return !!this.router.url.match(/^\/calendar/) || this.isMonitorCalendar();
  }

  /**
   * Commence à la première heure de conduite de la période et finit après la dernière,
   * sans jamais réduire la plage habituelle (8h-21h).
   */
  private fitSlotRange(events: Event[]) {
    const hours = events.map(e => moment(e.start, 'YYYY-MM-DD HH:mm'));
    const first = Math.min(DEFAULT_FIRST_HOUR, ...hours.map(h => h.hour()));
    const last = Math.max(DEFAULT_LAST_HOUR, ...hours.map(h => h.hour() + 1));
    const slotMinTime = this.toSlotTime(first);
    const slotMaxTime = this.toSlotTime(Math.min(24, last));
    // n'applique que si la plage change (évite un nouveau rendu inutile)
    if (this.calendarOptions.slotMinTime !== slotMinTime || this.calendarOptions.slotMaxTime !== slotMaxTime) {
      this.calendarOptions = {...this.calendarOptions, slotMinTime, slotMaxTime};
    }
  }

  private toSlotTime(hour: number): string {
    return (hour < 10 ? '0' : '') + hour + ':00:00';
  }

  /** Le calendrier occupe toute la hauteur visible sous sa position (barres d'outils comprises). */
  private fitHeight() {
    const top = this.elementRef.nativeElement.getBoundingClientRect().top + window.scrollY;
    // au moins MIN_CALENDAR_HEIGHT pour lire 8h-20h, même sur un petit écran (la page défile alors)
    const height = Math.max(MIN_CALENDAR_HEIGHT, Math.floor(window.innerHeight - top - 4));
    if (this.calendarOptions.height !== height) { // pas de nouveau rendu si rien ne change
      this.calendarOptions = {...this.calendarOptions, contentHeight: undefined, height};
    }
  }

  /** Planning personnel du moniteur (/monitor-calendar) : toujours modifiable par lui. */
  isMonitorCalendar(): boolean {
    return !!this.router.url.match('/monitor-calendar');
  }

  /** Lecteur sur le planning global : il ne peut agir que sur ses propres heures de moniteur. */
  isRestrictedToOwn(): boolean {
    return this.readOnlyService.isReadOnly() && !this.isMonitorCalendar();
  }

  /**
   * Moniteurs proposés à la modification d'une heure. Un moniteur (planning perso, ou lecteur sur le
   * planning global) ne peut garder que lui-même ou mettre l'heure sur le moniteur ABSENT (élève absent).
   */
  private editableMonitors(restricted: boolean): Monitor[] {
    if (restricted && this.myMonitor) {
      return [this.myMonitor, this.absentMonitor()];
    }
    if (this.isMonitorCalendar()) {
      return [...this.monitors.filter(m => m.id !== ABSENT_MONITOR_ID), this.absentMonitor()];
    }
    return this.monitors;
  }

  /** Le moniteur actuel de l'heure est toujours affiché dans « CHOISIR UN MONITEUR » (même désactivé). */
  private withEventMonitor(monitors: Monitor[], event: any): Monitor[] {
    const current: Monitor | undefined = event.extendedProps.monitor;
    return !current || monitors.some(m => m.id === current.id) ? monitors : [current, ...monitors];
  }

  private absentMonitor(): Monitor {
    return this.monitors.find(m => m.id === ABSENT_MONITOR_ID)
      ?? new Monitor(ABSENT_MONITOR_ID, 'ABSENT', 'ABSENT', '', '');
  }

  private isMine(event: any): boolean {
    return !!this.myMonitor && event.extendedProps.monitor?.id === this.myMonitor.id;
  }

  isMobilePhone() : boolean {
    return window.screen.width < 600;
  }

  handleDateClick(event:any) {
    // Lecteur sur le planning global : création uniquement sur son propre planning de moniteur
    if (this.isRestrictedToOwn() && !this.myMonitor) {
      return;
    }
    const monitors = this.isRestrictedToOwn() ? [this.myMonitor as Monitor] : this.monitors;
    $("#newEventModal").modal("show");
    let date = moment(event.date).format('YYYY-MM-DD');
    let hour = moment(event.date).format('HH:mm');
    $(".modal").find("#date").val(date);
    $(".modal").find("#hour").val(hour);
    this.eventNewComponent?.initNewEventForm(date, hour, monitors);
  }

  handleMonitorEvents(event:any) {
    $("#calendarMonitorModal").modal("show");

    this.eventsMonitorComponent?.initEventsMonitorForm(this.monitors);
  }

  handleGetInformations() {
    $("#calendarInformationsModal").modal("show");
  }

  handleDateChange(eventDates:any) {
    this.currentRange = eventDates;
    let dates : BetweenDates = new BetweenDates(eventDates.start, eventDates.end);
    this.loadUnavailabilities(dates);
    this.eventService.getEventsBetweenDates(dates, this.monitorId, this.isMonitorCalendar()).subscribe(
      (data: any[]) => {
        let events = data.map((item:EventTO) => {
          let props = new ExtendedProps(item.boxType, item.monitor, item.student, item.active, item.description);
          let student = item.student ? item.student.firstname + " " + item.student.lastname : "";
          let monitorName = item.monitor.firstname;
          let eventTitle = student != "" ? student + " | " + monitorName : monitorName;
          let color = student == "" ? COLOR_MANUAL_AVAILABLE : COLOR_MANUAL_RESERVED;
          if (item.boxType == "AUTOMATIQUE") {
            color = student == "" ? COLOR_AUTOMATIC_AVAILABLE : COLOR_AUTOMATIC_RESERVED;
          } else if (item.boxType == "PERMIS") {
            color = COLOR_PERMIS;
          }
          const iconInfo = item.description ? '💬' : '';

          return new Event(
            item.id,
            iconInfo + eventTitle,
            moment.utc(item.startAt).format('YYYY-MM-DD HH:mm'),
            moment.utc(item.startAt).add(1, "hours").format('YYYY-MM-DD HH:mm'),
            props, color);
        });
        this.calendarEvents = events;
        this.refreshEvents();
        this.fitSlotRange(events);
      },
      err => {
        console.log(err);
      }
    );

    this.eventService.getInfosBetweenDates(dates, this.monitorId).subscribe(
      (data: any[]) => {
        this.calendarInfos = data;
      },
      err => {
        console.log(err);
      })

  }

  handleEventClick(data:any) {
    let event = data.event;
    if (event.extendedProps.kind === 'unavailability') {
      this.openUnavailability(event.extendedProps.unavailability);
      return;
    }
    $("#editEventModal").modal("show");
    // Lecteur : modification seulement de ses propres heures (lui-même ou ABSENT)
    const restricted = this.isRestrictedToOwn() && this.isMine(event);
    if (this.eventEditComponent) {
      this.eventEditComponent.editable = !this.isRestrictedToOwn() || this.isMine(event);
    }
    this.eventEditComponent?.initEventEditForm(event, this.withEventMonitor(this.editableMonitors(restricted), event));
  }

  addNewEvent($event: EventTO) {
    let item = $event;
    let student = item.student ? item.student.firstname + " " + item.student.lastname : "";
    let monitorName = item.monitor.firstname;
    let eventTitle = student != "" ? student + " | " + monitorName : monitorName;
    let color = student == "" ? COLOR_MANUAL_AVAILABLE : COLOR_MANUAL_RESERVED;
    if ($event.boxType == "AUTOMATIQUE") {
      color = student == "" ? COLOR_AUTOMATIC_AVAILABLE : COLOR_AUTOMATIC_RESERVED;
    } else if ($event.boxType == "PERMIS") {
      color = COLOR_PERMIS;
    }
    let props = new ExtendedProps($event.boxType, $event.monitor, $event.student, $event.active, $event.description);
    let newEvent = new Event(
      $event.id,
      eventTitle,
      moment.utc($event.startAt).format('YYYY-MM-DDTHH:mm'),
      moment.utc($event.startAt).add(1, "hours").format('YYYY-MM-DDTHH:mm'),
      props,
      color
    );

    this.calendarEvents = [...this.calendarEvents, newEvent]
    this.refreshEvents();

    $('#newEventModal').modal("hide")
  }


  updateEvent($event: any) {
    let item = $event;
    let props = new ExtendedProps($event.boxType, $event.monitor, $event.student, $event.active, $event.description);
    let student = item.student ? item.student.firstname + " " + item.student.lastname : "";
    let monitorName = item.monitor.firstname;
    let eventTitle = student != "" ? student + " | " + monitorName : monitorName;
    let color = student == "" ? COLOR_MANUAL_AVAILABLE : COLOR_MANUAL_RESERVED;
    if ($event.boxType == "AUTOMATIQUE") {
      color = student == "" ? COLOR_AUTOMATIC_AVAILABLE : COLOR_AUTOMATIC_RESERVED;
    } else if ($event.boxType == "PERMIS") {
      color = COLOR_PERMIS;
    }
    let eventUpdated = new Event(
      $event.id,
      eventTitle,
      moment.utc($event.startAt).format('YYYY-MM-DDTHH:mm'),
      moment.utc($event.startAt).add(1, "hours").format('YYYY-MM-DDTHH:mm'),
      props,
      color
    );

    let index = this.calendarEvents.findIndex(e => e.id == $event.id);
    this.calendarEvents[index] = eventUpdated;
    this.refreshEvents();
    $('#editEventModal').modal("hide")
  }

  deleteEvent($eventId: string) {
    let index = this.calendarEvents.findIndex(e => e.id == $eventId);
    this.calendarEvents.splice(index, 1);
    this.calendarEvents = this.calendarEvents;
    this.refreshEvents();
    $('#editEventModal').modal("hide")
  }

  // ----- Indisponibilités des moniteurs -----

  /** Secrétariat (admin) : tous les moniteurs ; moniteur : lui-même. Un lecteur sans rôle moniteur ne fait que consulter. */
  canManageUnavailability(): boolean {
    return !this.readOnlyService.isReadOnly() || this.readOnlyService.hasRole('ROLE_MONITOR');
  }

  private canEditUnavailability(u: Unavailability): boolean {
    if (!this.canManageUnavailability()) {
      return false;
    }
    return !this.isRestrictedToOwn() || (!!this.myMonitor && this.myMonitor.id === u.monitorId);
  }

  /** Moniteurs proposés à la saisie : le moniteur lecteur ne gère que lui-même sur le planning global. */
  private unavailabilityMonitors(): Monitor[] {
    if (this.isRestrictedToOwn()) {
      return this.myMonitor ? [this.myMonitor] : [];
    }
    return this.monitors.filter(m => m.id !== ABSENT_MONITOR_ID);
  }

  openNewUnavailability() {
    const inRange = this.currentRange && moment().isBetween(this.currentRange.start, this.currentRange.end);
    const date = (inRange || !this.currentRange ? moment() : moment(this.currentRange.start)).format('YYYY-MM-DD');
    this.unavailabilityForm?.openNew(this.unavailabilityMonitors(), date);
  }

  private openUnavailability(u: Unavailability) {
    this.unavailabilityForm?.openExisting(u, this.unavailabilityMonitors(), !this.canEditUnavailability(u));
  }

  /** Après création / modification / suppression : rechargement de la période (créneaux libres supprimés compris). */
  onUnavailabilityChanged(message: string) {
    this.infoMessage = message;
    if (this.currentRange) {
      this.handleDateChange(this.currentRange);
    }
  }

  private loadUnavailabilities(dates: BetweenDates) {
    const monitorId = this.router.url.match('/monitors/[0-9]+') ? this.monitorId : undefined;
    this.unavailabilityService.list(dates, monitorId).subscribe(
      (data: Unavailability[]) => {
        this.unavailabilityEvents = data.map(u => this.toUnavailabilityEvent(u));
        // bandeau « journée entière » en haut des vues horaires, seulement s'il y a des journées indisponibles
        const allDaySlot = data.some(u => u.allDay);
        if (this.calendarOptions.allDaySlot !== allDaySlot) {
          this.calendarOptions = {...this.calendarOptions, allDaySlot};
        }
        this.refreshEvents();
      },
      err => console.log(err)
    );
  }

  /** Gris hachuré (classe fc-unavailability), avec l'icône du motif et le prénom du moniteur. */
  private toUnavailabilityEvent(u: Unavailability): any {
    const reason = UNAVAILABILITY_REASONS[u.reason];
    const monitor = u.monitorName.split(' ')[0];
    const format = u.allDay ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm';
    return {
      id: 'unavailability-' + u.id,
      title: `${reason.icon} ${monitor} — ${reason.label}${u.comment ? ' : ' + u.comment : ''}`,
      start: moment.utc(u.startAt).format(format),
      end: moment.utc(u.endAt).format(format),
      allDay: u.allDay,
      classNames: ['fc-unavailability'],
      backgroundColor: 'transparent',
      borderColor: '#adb5bd',
      textColor: '#343a40',
      extendedProps: {kind: 'unavailability', unavailability: u}
    };
  }

  /** Heures de conduite + indisponibilités. */
  private refreshEvents() {
    this.calendarOptions.events = [...this.calendarEvents, ...this.unavailabilityEvents];
  }

  getHoursTotal() {
    $('.fc-toolbar-title').mouseover()
  }


}
